import { loadImage } from './canvasRenderer';

// Recorta el fondo BLANCO de un logo generado por IA y devuelve un PNG con
// transparencia. La IA entrega el logo sobre blanco plano (ver logoGenPresets.ts);
// acá lo volvemos usable sobre cualquier template.
//
// Técnica: flood-fill desde las 4 esquinas. Solo se vuelve transparente el blanco
// CONECTADO al borde, así los blancos INTERNOS del logo (contraformas, detalles)
// se preservan. Un thresholding global plano se comería esos blancos internos.

interface RemoveBgOptions {
  // Un pixel cuenta como "blanco de fondo" si sus 3 canales superan este umbral.
  whiteThreshold?: number; // default 235
}

export async function removeWhiteBackground(imageUrl: string, opts: RemoveBgOptions = {}): Promise<Blob> {
  const whiteThreshold = opts.whiteThreshold ?? 235;

  // loadImage proxya por /api/proxy/image → mismo origen → canvas sin taint.
  const img = await loadImage(imageUrl);
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  if (!w || !h) throw new Error('Logo image has no dimensions');

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.drawImage(img, 0, 0);

  const imageData = ctx.getImageData(0, 0, w, h);
  const data = imageData.data;

  const isWhite = (idx: number): boolean =>
    data[idx] >= whiteThreshold && data[idx + 1] >= whiteThreshold && data[idx + 2] >= whiteThreshold;

  // BFS/DFS desde el borde. `visited` evita reprocesar; `background` marca el
  // blanco conectado al borde (lo que se vuelve transparente).
  const visited = new Uint8Array(w * h);
  const background = new Uint8Array(w * h);
  const stack: number[] = [];

  const seed = (x: number, y: number) => {
    if (x < 0 || y < 0 || x >= w || y >= h) return;
    const p = y * w + x;
    if (visited[p]) return;
    visited[p] = 1;
    if (isWhite(p * 4)) stack.push(p);
  };

  for (let x = 0; x < w; x++) {
    seed(x, 0);
    seed(x, h - 1);
  }
  for (let y = 0; y < h; y++) {
    seed(0, y);
    seed(w - 1, y);
  }

  while (stack.length) {
    const p = stack.pop() as number;
    background[p] = 1;
    const x = p % w;
    const y = (p / w) | 0;
    seed(x + 1, y);
    seed(x - 1, y);
    seed(x, y + 1);
    seed(x, y - 1);
  }

  // 1) Fondo → transparente.
  for (let p = 0; p < w * h; p++) {
    if (background[p]) data[p * 4 + 3] = 0;
  }

  // 2) Anti-halo: pixeles conservados, casi blancos y tocando el fondo, suelen ser
  // el borde antialiased del blanco → bajamos su alpha para que no quede un orillo.
  const haloLum = whiteThreshold - 12;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const p = y * w + x;
      if (background[p]) continue;
      const idx = p * 4;
      if (data[idx + 3] === 0) continue;
      const touchesBg =
        (x > 0 && background[p - 1]) ||
        (x < w - 1 && background[p + 1]) ||
        (y > 0 && background[p - w]) ||
        (y < h - 1 && background[p + w]);
      if (!touchesBg) continue;
      const lum = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
      if (lum >= haloLum) data[idx + 3] = Math.round(data[idx + 3] * 0.35);
    }
  }

  ctx.putImageData(imageData, 0, 0);

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Failed to export PNG'))), 'image/png');
  });
}

// Nombre de archivo "lindo" para el File del logo (slug de la marca).
export function blobToLogoFile(blob: Blob, brandName: string): File {
  const slug =
    brandName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '') || 'logo';
  return new File([blob], `${slug}-logo.png`, { type: 'image/png' });
}
